import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import CalendarIcon from "@/components/SVGComponents/CalendarIcon";
import LocationPinIcon from "@/components/SVGComponents/LocationPinIcon";
import ImagePlaceholderIcon from "@/components/SVGComponents/ImagePlaceholderIcon";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import { TableCellTooltip, Tooltip } from "@/components/Tooltip";
import type { Event } from "@/services/api/events";
import routes from "@/routes/routes";
import {
  EVENT_TYPE_LABELS,
  formatEventDate,
  formatEventTime,
  formatTicketPrice,
  GATHERING_STATUS_TONE,
  getEventCityName,
  getPublishingLabel,
  resolveThumbnailUrl,
} from "../../lib/eventFormat";
import styles from "./GatheringCard.module.scss";

interface GatheringCardProps {
  event: Event;
}

const GatheringCard = ({ event }: GatheringCardProps) => {
  const navigate = useNavigate();
  const statusLabel = getPublishingLabel(event.publishingStatus);
  const typeLabel: string = EVENT_TYPE_LABELS[event.eventType] ?? event.eventType ?? "";
  const city = getEventCityName(event);
  const thumbnailUrl = resolveThumbnailUrl(event.thumbnailUrl);
  const [imageFailed, setImageFailed] = useState(false);
  const showThumbnail = thumbnailUrl && !imageFailed;
  const filledCapacity = event.filledCapacity ?? 0;
  const fillPercent = event.capacity > 0 ? Math.min(100, Math.round((filledCapacity / event.capacity) * 100)) : 0;

  const goToDetails = () => navigate(routes.privateRoutes.GATHERING_DETAILS.path.replace(":eventId", String(event.id)));

  return (
    <div
      className={styles.card}
      onClick={goToDetails}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          goToDetails();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className={styles.banner}>
        {showThumbnail && (
          <>
            <img
              src={thumbnailUrl}
              alt=""
              className={styles.bannerImage}
              onError={() => setImageFailed(true)}
            />
            <div className={styles.bannerOverlay} />
          </>
        )}
        {typeLabel && (
          <span className={styles.typeBadge}>
            <span className={styles.typeDot} />
            {typeLabel.toUpperCase()}
          </span>
        )}
        {!showThumbnail && <ImagePlaceholderIcon className={styles.bannerIcon} />}
      </div>

      <div className={styles.body}>
        <h3 className={styles.name}>
          <TableCellTooltip value={event.name} />
        </h3>

        <div className={styles.metaRow}>
          <CalendarIcon className={styles.metaIcon} />
          <span>
            {formatEventDate(event.eventDate)} · {formatEventTime(event.eventStartTime)}–
            {formatEventTime(event.eventEndTime)}
          </span>
        </div>
        <div className={styles.metaRow}>
          <LocationPinIcon className={styles.metaIcon} />
          <span>{city || "Location TBA"}</span>
        </div>

        <div className={styles.rsvpRow}>
          <span>RSVPs</span>
          <span className={styles.rsvpValue}>
            {filledCapacity} / {event.capacity || "—"}
          </span>
        </div>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${fillPercent}%` }} />
        </div>

        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <StatusBadge label={statusLabel.toUpperCase()} tone={GATHERING_STATUS_TONE[statusLabel] ?? "neutral"} />
            <span className={styles.priceLabel}>{formatTicketPrice(event.ticketPrice)}</span>
          </div>
          <button
            type="button"
            className={styles.manageLink}
            onClick={(e) => {
              e.stopPropagation();
              navigate(routes.privateRoutes.GATHERINGS_EDIT.path.replace(":eventId", String(event.id)));
            }}
          >
            MANAGE
            <ArrowIcon className={styles.manageIcon} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GatheringCard;
